<template>
  <div id="accordionTourBeforeTour" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourBeforeTour" class="accordion-header">
        <button
          aria-controls="collapseTourBeforeTour"
          aria-expanded="true"
          class="accordion-button"
          data-bs-target="#collapseTourBeforeTour"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="template-setup-accordion-icon me-8 d-flex align-items-center justify-content-center">
              <v-icon icon="contact" size="xs" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.activity.beforeTrip") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.activity.beforeTripSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourBeforeTour"
        aria-labelledby="headingTourBeforeTour"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourBrforeTour"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <nav class="translations-tab mb-8">
            <div id="nav-tab" class="nav nav-tabs border-bottom-0" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-template-day-update-basic-information-${language.key}`"
                :key="index"
                :aria-controls="`template-day-update-basic-information-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#template-day-update-basic-information-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-4 px-12 me-8 text-xs"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="case-tour-update-before-update-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("clientTour.beforeTour") }}</span>
                </label>
                <v-html-editor
                  :id="'case-tour-update-before-update-' + language.shortName"
                  ref="case-tour-update-before-update-ref"
                  :value="beforeTrip[language.shortName]?.description"
                  @input="handleInput(language.shortName, $event)"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
              </div>
            </fieldset>

            <v-button
              :is-loading="accepted"
              :disabled="disableButton"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Template from "@/api/objects/Template";
import { createLocaleTranslationMap, useAppLanguages } from "@/composables/useAppLanguages";
import { useToast, POSITION } from "vue-toastification";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  template: {
    type: Object as () => Template,
    required: true,
  },
});

const emit = defineEmits(["reloadTemplate"]);
const toast = useToast();

// Build beforeTrip as a reactive object
const beforeTrip = createLocaleTranslationMap(() => ({ description: "" }));

const disableButton = ref<boolean>(true);
const editorMedia = ref<any[]>([]);
const { languages, resolveStoredLocale } = useAppLanguages();
const currentLanguage = ref(resolveStoredLocale(localStorage.getItem("language")));

const form = useFormValidation("", beforeTrip);
const { accepted, toggleAccepted } = form;

// Helper to update disableButton state
function updateDisableButton() {
  disableButton.value = Object.values(beforeTrip).some((locale) => !locale.description || locale.description.trim() === "");
}

const beforeTripData = (props.template as any)?.beforeInformation?.translations || [];
beforeTripData.forEach((translation: { locale: string; description: string }) => {
  const { locale, description } = translation;
  if (beforeTrip[locale]) {
    beforeTrip[locale].description = description;
  }
});

const handleInput = (language: string, event: string) => {
  if (beforeTrip[language]) {
    beforeTrip[language].description = event;
  }
  updateDisableButton();
};

const updateTemplate = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      name: props.template.name,
      travel_type: props.template.travelType,
      origin: props.template.originId,
      destination: props.template.destinationId,
      contact_text: props.template.contactText,
      terms_conditions_text: props.template.termsConditionsText,
      before_trip: beforeTrip,
    };

    const template = await api.activityService.updateActivity(props.agency, props.template.id, data);

    if (editorMedia.value.length > 0) {
      const editorMediaData = {
        media: editorMedia.value,
        model_type: "template",
        collection: "tinymce",
        model_id: props.template.id,
      };
      await api.media.confirm("s3", editorMediaData);
    }

    toast.success("Saved successfully", { position: POSITION.BOTTOM_RIGHT });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || e?.message || "An error occurred", {
      position: POSITION.BOTTOM_RIGHT,
    });
  } finally {
    toggleAccepted();
  }
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.9375rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.8125rem !important;
}
</style>
